"use client";

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  clearCoupon,
  isCouponApplied,
  selectAppliedCouponCode,
} from "../../redux/couponSlice";
import {
  SelectTransactionType,
  SelectActualAmount,
  SelectEnteredAmount,
  SelectMetalQuantity,
  SelectTotalAmount,
  SelectPurchaseType,
} from "../../redux/shopSlice";
import SelectUpiModalForPayout from "./sellSelectUpiModal";
import ProgressBar from "../progressBar";
import { XMarkIcon } from "@heroicons/react/20/solid";
import mixpanel from "mixpanel-browser";
import { useRouter } from "next/navigation";

// Define prop types
interface ModalProps {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  transactionId: string;
  previewData: { key: string; value: string }[];
}

// Define transaction types for clarity
type TransactionType = "buy" | "sell";

export default function Modal({ isOpen, onClose, transactionId, previewData }: ModalProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Redux state selectors
  const metalType = useSelector((state: RootState) => state.shop.metalType);
  const transactionType = useSelector(SelectTransactionType);
  const purchaseType = useSelector(SelectPurchaseType) as TransactionType;
  const enteredAmount = useSelector(SelectEnteredAmount);
  const totalAmount = useSelector(SelectTotalAmount);
  const metalQuantity = useSelector(SelectMetalQuantity);
  const isAnyCouponApplied = useSelector(isCouponApplied);
  const appliedCouponCode = useSelector(selectAppliedCouponCode);
  const gst = useSelector((state: RootState) => state.shop.gst);
  const extraGoldOfRuppess = useSelector((state: RootState) => state.coupon.extraGoldOfRuppess);
  const actualAmount = useSelector(SelectActualAmount);
  const extraGold = useSelector((state: RootState) => state.coupon.extraGold);

  // Local state
  const [isPayoutModalOpen, setPayoutModalOpen] = useState(false);

  // Derived data
  const welcomeGold = previewData.find((item) => item.key === "Welcome GOLD")?.value.replace(/ gm/g, "");

  // Event tracking with Mixpanel
  useEffect(() => {
    if (!mixpanel.hasOwnProperty("__loaded")) return; // Ensure Mixpanel is initialized

    const eventName = purchaseType === "buy" ? "Buy Modal(web)" : "Sell Modal(web)";
    const eventProperties = {
      transaction_Type: transactionType,
      order_type: purchaseType,
      metal_Type: metalType,
      metal_Quantity: metalQuantity,
      amount: totalAmount,
      ...(purchaseType === "buy" && {
        applied_Coupon_Code: isAnyCouponApplied ? appliedCouponCode : "not applied",
      }),
    };

    mixpanel.track(eventName, eventProperties);

    if (welcomeGold) {
      dispatch(clearCoupon());
    }
  }, [dispatch, purchaseType, transactionType, metalType, metalQuantity, totalAmount, isAnyCouponApplied, appliedCouponCode, welcomeGold]);

  // Handlers
  const handleClose = useCallback(() => onClose(false), [onClose]);

  const handleBuyNow = useCallback(async () => {
    try {
      const encodedOrderId = btoa(transactionId);
      router.push(`/checkout/${encodedOrderId}?itemMode=DIGITAL`, { scroll: false });
    } catch (error) {
      console.error("Error navigating to checkout:", error);
    }
  }, [router, transactionId]);

  const handleOpenPayoutModal = useCallback(() => setPayoutModalOpen(true), []);
  const handleClosePayoutModal = useCallback(() => {
    setPayoutModalOpen(false);
    handleClose();
  }, [handleClose]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        className="fixed inset-0 z-[110] overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => false} // Disable backdrop close
        static // Prevent ESC key close
      >
        {/* Nested Payout Modal */}
        {isPayoutModalOpen && (
          <SelectUpiModalForPayout
            isOpen={isPayoutModalOpen}
            onClose={handleClosePayoutModal}
            transactionId={transactionId}
          />
        )}

        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true" />
        </TransitionChild>

        {/* Modal Content */}
        <div className="fixed inset-0 z-50 flex min-h-full items-center justify-center p-4 sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel className="relative coins_backgroun w-full max-w-lg transform overflow-hidden rounded-lg coins-backgroun text-left text-white shadow-xl transition-all sm:my-8">
              {/* Header */}
              <div className="px-4 pb-4 pt-5 sm:p-4 sm:pb-4">
                <div className="mb-4 flex items-center gap-4 pt-4">
                  <img
                    src={metalType === "gold" ? "/Goldbarbanner.png" : "/Silverbar.png"}
                    alt={`${metalType === "gold" ? "Gold" : "Silver"} Bar`}
                    className="h-5"
                  />
                  <p className="text-base sm:text-xl">
                    {purchaseType === "buy" ? "Price" : "Sell"} Breakdown
                  </p>
                </div>

                {/* Breakdown List */}
                <ul className="space-y-1">
                  {previewData.map((item, index) => (
                    <li key={item.key || index}>
                      <div className="flex justify-between border-b border-dashed border-gray-400 py-1">
                        <span>{item.key}</span>
                        <span className="text-sm font-bold text-blue-100 sm:text-base">{item.value}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Progress Bar */}
              <div className="pt-4 pb-2">
                <ProgressBar
                  purchaseType={purchaseType}
                  fromCart={true}
                  metalTypeForProgressBar={metalType}
                  displayMetalType={metalType}
                />
              </div>

              {/* Footer */}
              <div className="flex justify-between px-4 py-2 sm:px-6">
                <button
                  type="button"
                  ref={cancelButtonRef}
                  className="absolute right-3 top-3 rounded-full coins-backgroun p-2 text-sm font-bold text-white shadow-sm ring-1 ring-gray-300 hover:bg-themeBlue focus:outline-none focus:ring-2 focus:ring-themeBlue"
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <div className="flex w-full items-center justify-between tracking-wider">
                  <div>
                    <p className="text-md text-blue-100">Total Amount</p>
                    <p className="text-xl font-extrabold tracking-wide text-blue-100 sm:text-2xl">
                      ₹ {transactionType === "grams" ? totalAmount : enteredAmount}
                      {purchaseType === "buy" && (
                        <span className="ml-1 text-sm font-thin tracking-tighter text-gray-400">
                          (Incl. GST)
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    {purchaseType === "buy" ? (
                      <button
                        className="inline-flex justify-center rounded-md bg-themeBlue px-3 py-2 text-sm font-bold text-black shadow-sm ring-1 ring-gray-300 hover:bg-themeBlue-dark focus:outline-none focus:ring-2 focus:ring-themeBlue sm:text-xl"
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </button>
                    ) : (
                      <button
                        className="inline-flex justify-center rounded-md bg-themeBlue px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-themeBlue-dark focus:outline-none focus:ring-2 focus:ring-themeBlue"
                        onClick={handleOpenPayoutModal}
                      >
                        Proceed to Sell
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}